/**
 * Worker route payloads (docs/05).
 *
 * All responses are Bangla-first: any user-facing string in a response body is
 * already localised, because the client must never compose Bangla from fragments.
 */

import type { FulfilmentMode } from './money.js';
import type { Confidence } from './ai.js';

export const MAX_PAGE_LIMIT = 50;

export interface Page<T> {
  items: T[];
  cursor: string | null;
}

/* ---------------------------------------------------------------- search --- */

/**
 * Search is one door (audit finding 01). The user cannot be expected to know
 * whether a sentence belongs in search or in the AI tab, so the Worker decides
 * server-side and the client never routes.
 */
export interface SearchRequest {
  q: string;
  /**
   * The area `nearby` is measured from. Optional: for a signed-in caller the Worker reads
   * the profile's own area (`identities.area_id`) when this is absent.
   */
  area_id?: string;
  mode?: 'auto';
  /**
   * B3's four chips, `সব · কাছাকাছি · যাচাইকৃত · আজ`. Absent means `all`.
   *
   * A chip that only changed its own highlight while the list stayed the same was a
   * control that lied about what it did; the filter now travels to the Worker and the
   * list is re-asked.
   */
  filter?: SearchFilter;
}

/**
 * The four filters B3 draws, as machine keys. The Bangla labels are the frame's and stay
 * on the screen that draws them (`apps/web/src/routes/search/data.ts § RESULT_FILTERS`).
 *
 *   all       no filter
 *   nearby    a provider that serves the caller's area, inside it, or an area containing it
 *   verified  a provider with a `verified` outcome in `provider_verifications`
 *   today     live, not fully booked, and an arrival time stated in minutes under a day
 */
export const SEARCH_FILTERS = ['all', 'nearby', 'verified', 'today'] as const;
export type SearchFilter = (typeof SEARCH_FILTERS)[number];

export function isSearchFilter(value: unknown): value is SearchFilter {
  return typeof value === 'string' && (SEARCH_FILTERS as readonly string[]).includes(value);
}

export type SearchRoute = 'lookup' | 'ai' | 'domain_not_live';

export interface SearchIntent {
  domain_id: number;
  intent_bn: string;
  confidence: Confidence;
}

export interface SearchGroup {
  type: string;
  /**
   * The drawn heading for this group — `টেকনিশিয়ান`, `সার্ভিস প্যাকেজ`.
   *
   * EMPTY when no frame draws a heading for this offering type. The screen renders
   * nothing in place of an empty string and the response reports the gap through
   * `copy_status` / `needs`; it is never filled in by the client.
   */
  label_bn: string;
  /**
   * How many of this type the catalogue holds for this query — NOT `items.length`.
   *
   * B3 draws `টেকনিশিয়ান ১২ জন` above two cards: the heading counts the group, the list
   * shows the page of it that came back. Collapsing the two would make the heading say
   * how many rows fitted rather than how many exist, which is the opposite of the frame
   * note's purpose ("so the user can judge whether the group is worth opening").
   */
  count: number;
  /**
   * `count`, as the Bangla phrase the frame draws — `১২ জন`, `৩টি`.
   *
   * A phrase and not a number because the counter differs by what is being counted:
   * `জন` for people, `টি` for things. A client turning 12 into `১২ জন` is composing
   * Bangla and choosing between `জন` and `টি` is composing grammar, so the whole phrase
   * is the server's (docs/05 § conventions). Empty or absent when nobody drew one.
   */
  count_bn?: string | null;
  items: unknown[];
}

/**
 * One result card, in the shape B3 draws it — `SearchGroup.items` from the catalogue rungs.
 *
 * A FIELD THE CATALOGUE DOES NOT HOLD IS OMITTED, never defaulted: no `verified_label_bn`
 * means not verified, no `amount_minor` means no price (never `৳ 0`).
 *
 * `slug` and `to` are what make the card open. `offering_id` is the database's uuid and D1
 * resolves an offering by `offering_presentation.slug`, so a card that carried only the
 * uuid could not have been a link even if it had been drawn as one. `to` is the app path
 * the card opens — `/offering/<slug>` — composed once, server-side, so the client never
 * builds a URL from fragments. An offering with no presentation row has no page, and its
 * card carries neither field: it renders, and it does not pretend to open.
 */
export interface SearchCard {
  offering_id: string;
  slug?: string;
  to?: string;
  title_bn: string;
  meta_bn?: string;
  verified_label_bn?: string;
  rating_bn?: string;
  jobs_bn?: string;
  eta_bn?: string;
  amount_minor?: number;
}

/**
 * What became of the filter the request asked for.
 *
 * `applied: false` is said out loud rather than answered with the unfiltered list: a
 * `কাছাকাছি` chip drawn selected above results that are not near anything is the chip
 * lying. The one way it is not applied today is `nearby` with no area known — no area in
 * the request and none on the profile — and then `needs_area` is set, the list is empty,
 * and `message_bn` / `action_bn` / `to` say where the area is entered.
 *
 * `message_bn` is also sent when a filter WAS applied and matched nothing, so the screen
 * says so instead of drawing chrome above an empty column.
 *
 * Every sentence here is AUTHORED (`copy_status: 'authored'`): no frame draws either
 * moment. See `packages/contracts/src/authored-copy/search.ts`.
 */
export interface SearchFilterState {
  id: SearchFilter;
  applied: boolean;
  needs_area?: boolean;
  message_bn?: string;
  action_bn?: string;
  to?: string;
  copy_status?: 'authored';
}

/** One row of the browse answer — a category of the matched domain, or a live domain. */
export interface SearchBrowseLink {
  label_bn: string;
  /** `/explore/<domain>/<category>` or `/explore/<domain>`. */
  to: string;
}

/**
 * The answer that always exists (docs/06, rung 5), now with something in it.
 *
 * It used to be an empty results column under the chrome. When the catalogue has nothing
 * for the query, the Worker sends the matched domain's categories — or, when no domain
 * matched at all, the live domains — and one sentence saying so. The sentence is
 * AUTHORED; the labels are the taxonomy's own (`categories.label_bn`,
 * `life_domains.label_bn`).
 */
export interface SearchBrowse {
  /** The matched domain, or 0 when the query matched none and `links` are domains. */
  domain_id: number;
  sentence_bn: string;
  copy_status: 'authored';
  links: SearchBrowseLink[];
}

/**
 * A signed-out caller whose sentence the Worker read as a conversation.
 *
 * A conversation lives in an account (`conversations.identity_id` is not null), so the
 * answer is a sign-in door rather than an empty results screen. The client sends the
 * person to sign in with the path they were on — which carries `q` — and signing in brings
 * them back to the same search, which the Worker then hands to the conversation.
 */
export interface SearchSignIn {
  title_bn: string;
  body_bn: string;
  action_bn: string;
  copy_status: 'authored';
}

export interface SearchResponse {
  route: SearchRoute;
  /**
   * Rendered above the answer with a "না, অন্য কিছু" correction control
   * (audit finding 07). Making the understood intent visible turns catching a
   * mistake and fixing it into the same tap.
   */
  intent: SearchIntent;
  groups: SearchGroup[];
  conversation_id?: string;
  /** Present whenever the request carried a filter other than `all`. */
  filter?: SearchFilterState;
  /** Present when a `lookup` found nothing in the catalogue and no filter explains why. */
  browse?: SearchBrowse;
  /** Present when `route` is `ai` and nobody is signed in. */
  sign_in?: SearchSignIn;
}

/* ------------------------------------------------ B2 · recents and popular --- */

/** `GET /v1/search/recent` — the signed-in caller's own recent searches, newest first. */
export interface SearchRecentItem {
  /** Exactly what they typed. Their own words, never another person's. */
  query_bn: string;
  searched_at: string;
}

export interface SearchRecentResponse {
  items: SearchRecentItem[];
}

/**
 * `GET /v1/search/popular` — B2's `মানুষ যা খোঁজেন`.
 *
 * `source` says where the chips came from: `demand` when every one is a phrase people
 * actually searched for this week (counted in `demand_rollups`), `drawn` when there was no
 * such demand and the five B2 draws stand in, `mixed` when demand filled some and the drawn
 * five the rest. A label is only ever a phrase from the keyword table — never a sentence a
 * customer typed, which is theirs and not a chip on somebody else's screen.
 */
export interface SearchPopularResponse {
  items: { label_bn: string }[];
  source: 'demand' | 'drawn' | 'mixed';
}

/* ---------------------------------------------- the eleventh state · notify --- */

/** `POST /v1/search/notify` — "চালু হলে জানান", for a signed-in caller. */
export interface SearchNotifyRequest {
  domain_id: number;
}

export interface SearchNotifyResponse {
  domain_id: number;
  /**
   * `subscribed` — a `domain_launch_subscriptions` row exists, and an in-app notification
   * is written for it the moment `life_domains.is_live` flips (0640).
   * `already_live` — nothing to wait for; the domain is live now.
   */
  state: 'subscribed' | 'already_live';
  message_bn: string;
  copy_status: 'authored';
}

/** The correction control's fixed label (audit finding 07). */
export const INTENT_CORRECTION_LABEL_BN = 'না, অন্য কিছু';

/* ------------------------------------------------ the eleventh state --- */

/**
 * One row of `এখন যা করতে পারেন` — a thing that DOES work, and where it works.
 *
 * ── WHY THIS IS NOT `DomainNotLiveError['alternatives'][number]` ─────────────
 * `errors.ts` types the error envelope's alternatives as `{ offering_id, title_bn }`,
 * and the drawn state needs something else. `P0 States § ১১` draws each row as a LABEL
 * plus where it is available — "ঔষধ কিনুন · কেনাকাটায় চালু আছে",
 * "অ্যাম্বুলেন্সের নম্বর · ফ্রি রিসোর্স · কল করা যাবে" — and the second of those is a
 * free resource, not an offering, so `offering_id` cannot name it. The two shapes are
 * both real and they are not the same shape; this is the one the screen renders.
 *
 * FLAGGED for whoever reconciles them: either the envelope's payload grows a `where_bn`
 * and an id that is not required to be an offering, or the two stay separate and
 * `errors.ts` says which surface each belongs to.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export interface SearchAlternative {
  /** A machine key for the row. NOT necessarily an offering id — see above. */
  id: string;
  label_bn: string;
  where_bn: string;
  /**
   * Where the row opens — `/offering/<slug>` of a live listing the catalogue holds NOW.
   *
   * Required, because a row under `এখন যা করতে পারেন` is a claim that the thing works
   * today. The Worker finds the listing before it sends the row, and a row it cannot back
   * with one is not sent at all rather than sent as a button that goes nowhere.
   */
  to: string;
}

/**
 * The payload `POST /v1/search` sends with `route: "domain_not_live"` — the eleventh
 * state, whose whole job is to be useful rather than apologetic (docs/05).
 *
 * Declared HERE, once, because three places need the same shape and were declaring it
 * separately: the Worker that composes it, the web client that renders it, and the
 * fixture the visual gate serves. Every string is already localised; the client composes
 * none of them.
 *
 * AN EMPTY STRING IS MEANINGFUL. The server sends `''` for a sentence that does not
 * exist rather than inventing one — the state is drawn for one of the seven unlaunched
 * domains, and the demand line is withheld when the row was not actually written — and
 * marks the response `copy_status: 'copy_missing'` with what is needed. A surface
 * renders nothing in place of an empty string; it never fills one in.
 */
export interface SearchDomainNotLive {
  title_bn: string;
  body_bn: string;
  /** Always true. docs/05 § errors requires it on this state. */
  notify_available: boolean;
  alternatives: readonly SearchAlternative[];
  /**
   * The demand-signal acknowledgement. Present only when the `demand_signals` row was
   * actually written: it is a claim about a row, and making it when the write failed
   * would be a lie told in Bangla on the user's screen.
   */
  demand_note_bn: string;
  /**
   * `পাঁচটি ক্ষেত্র এখন চালু · সাতটি আসছে` — sent only while it is TRUE.
   *
   * The count is `life_domains.is_live` now, not a constant, and the drawn sentence states
   * five and seven in words. When the table says anything else the sentence would be false,
   * so it is sent empty rather than edited: the footer renders nothing.
   */
  coverage_bn: string;
}

/**
 * Server-side routing heuristic (docs/05 § search). Kept in contracts so the
 * client's tests can assert the client never makes this decision itself.
 */
export function isConversational(q: string): boolean {
  const tokens = q.trim().split(/\s+/).filter(Boolean).length;
  return tokens > 3 || q.trimEnd().endsWith('?');
}

/* ------------------------------------------------------------- checkout --- */

export interface FeeRow {
  label_bn: string;
  amount_minor: number;
}

/**
 * The full fee breakdown, returned BEFORE commitment.
 *
 * The client renders exactly the rows it receives and computes nothing. A fee the
 * server forgets is a fee the customer discovers, and "সম্পূর্ণ ফি প্রতিশ্রুতির আগে"
 * is a brand rule with no exceptions (docs/05 § checkout).
 */
export interface CheckoutQuote {
  subtotal_minor: number;
  fees: FeeRow[];
  total_minor: number;
  fulfilment: FulfilmentMode;
  /** Required unless fulfilment is 'licensed'. Displayed on every money screen. */
  partner_name: string | null;
  refund_policy_key: string;
  disclosure_bn: string;
}

/**
 * Verify a quote is internally consistent before rendering it.
 *
 * This is a guard against the client quietly "fixing" a server total by
 * recomputing it. If these disagree, the correct response is to fail loudly —
 * a checkout screen showing a total the server did not send is the one bug that
 * turns a fee disclosure rule into a broken promise.
 */
export function quoteTotalsAgree(q: CheckoutQuote): boolean {
  const fees = q.fees.reduce((sum, f) => sum + f.amount_minor, 0);
  return q.subtotal_minor + fees === q.total_minor;
}

/* ------------------------------------------------------- verification --- */

export interface VerificationCheck {
  check_key: string;
  label_bn: string;
  method_bn: string | null;
  checked_at: string | null;
}

/**
 * Both arrays are ALWAYS returned (docs/05, audit finding 04). An empty
 * `not_checked` array is a claim, so it had better be true.
 *
 * Listing the gaps is what makes the badge trustworthy; a badge implying
 * everything was checked transfers the provider's risk onto Oprappo.
 */
export interface VerificationResponse {
  verified: VerificationCheck[];
  not_checked: VerificationCheck[];
  checked_at: string | null;
}

/* ------------------------------------------------------------ activity --- */

export interface ActivityRow {
  id: string;
  kind: string;
  status_key: string;
  label_bn: string;
  amount_minor: number | null;
  updated_at: string;
  /**
   * Present on every row so the client can label rows individually during a sync
   * failure. A per-row timestamp is unforgettable in a way a dismissed banner is not
   * (docs/05 § activity).
   */
  stale_at: string | null;
}

/* -------------------------------------------------------------- founder --- */

/**
 * The brief returns EXACTLY three items. Enforced server-side.
 *
 * A list of ten has no priority in it, and the layout depends on the cap.
 */
export const BRIEF_ITEM_COUNT = 3;

/** Intelligence groups are capped at three items each, for the same reason. */
export const INTELLIGENCE_GROUP_CAP = 3;

export interface BriefItem {
  id: string;
  title_bn: string;
  why_bn: string;
  evidence: unknown;
}

export interface FounderBrief {
  state_bn: string;
  items: BriefItem[];
  decisions: unknown[];
  panels: unknown[];
}

export function assertBriefShape(b: FounderBrief): void {
  if (b.items.length !== BRIEF_ITEM_COUNT) {
    throw new Error(`brief must carry exactly ${BRIEF_ITEM_COUNT} items, got ${b.items.length}`);
  }
}

export interface ApprovalDecision {
  approval_id: string;
  decision: 'approve' | 'reject';
  /**
   * REQUIRED when the approval carries a policy_conflict_bn. The decide endpoint
   * rejects the request without it — the constitution is a rule, not a document
   * (docs/05, docs/06 § constitution).
   */
  decision_reason_bn?: string;
}

export function decisionIsWellFormed(
  d: ApprovalDecision,
  policyConflictBn: string | null,
): boolean {
  if (policyConflictBn === null) return true;
  return typeof d.decision_reason_bn === 'string' && d.decision_reason_bn.trim().length > 0;
}
